import React, { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import DataTable from "react-data-table-component";
import Select, { components } from "react-select";
import AsyncSelect from "react-select/async";
import { useDebouncedCallback } from "use-debounce";
import { useAuth0 } from "@auth0/auth0-react";
import { toast } from "react-toastify";

import ai from "../../helpers/axios";
import Loader from "../loader";
import PageHeaderRowSearch from "../shared/page-header-row-search";
import shortenName from "../../helpers/shorten-name";
import currencyFormatter from "../../helpers/currencyFormatter";
import { formatTripDate } from "../../helpers/dates";
import TripVerificationExpanded from "./verification/trip-verification-expanded";
import axios from "axios";
import { Link } from "react-router-dom";
import Tooltip from "@mui/material/Tooltip";

const PER_PAGE_OPTIONS = [10, 25, 50, 100];
const PROSPECT_SEARCH_LIMIT = 50;

const STAGE_LABELS = {
  ended: "Ended",
  canceled: "Canceled",
  archived: "Archived",
  newTrip: "Active",
  closedPassthrough: "Passthrough",
};

const STATUS_FILTER_OPTIONS = [
  { label: "Verified", value: "verified" },
  { label: "Not Verified", value: "notVerified" },
];

const CompactValueContainer = ({ children, getValue, hasValue, ...props }) => {
  const selected = getValue();
  const count = selected.length;

  if (!hasValue || count <= 1) {
    return (
      <components.ValueContainer {...props} getValue={getValue} hasValue={hasValue}>
        {children}
      </components.ValueContainer>
    );
  }

  const childArray = React.Children.toArray(children);
  const input = childArray[childArray.length - 1];
  const firstValue = childArray[0];

  return (
    <components.ValueContainer {...props} getValue={getValue} hasValue={hasValue}>
      {firstValue}
      <span className="select-extra-count">+{count - 1}</span>
      {input}
    </components.ValueContainer>
  );
};

const COMPACT_SELECT_COMPONENTS = { ValueContainer: CompactValueContainer };

const conditionalRowStyles = [
  {
    when: (row) => row.verified,
    style: {
      backgroundColor: "#9ce19b !important",
      "&:hover": {
        backgroundColor: "#7fd07e !important",
      },
    },
  },
  {
    when: (row) => !row.verified && row.lockedComputed,
    style: {
      backgroundColor: "#f2c7c6 !important",
      "&:hover": {
        backgroundColor: "#e5adab !important",
      },
    },
  },
];

const formatLeg = (leg) => {
  if (!leg) return "N/A";
  const date = formatTripDate(leg.departureDate);
  const dep = leg.departureAirport?.code || "";
  const arr = leg.arrivalAirport?.code || "";
  return `${date} ${dep}-${arr}`.trim();
};

const formatCurrency = (value) =>
  value === null || value === undefined ? "N/A" : currencyFormatter.format(value);

const reducer = (state, action) => {
  switch (action.type) {
    case "fieldChanged":
      return { ...state, [action.field]: action.value };
    case "setLoading":
      return { ...state, loading: action.payload };
    case "dataDownloaded":
      return {
        ...state,
        loading: false,
        data: action.payload?.trips || [],
        totalLength: action.payload?.countTotal || 0,
      };
    case "pageChanged":
      return { ...state, currentPage: action.payload };
    case "rowsPerPageChanged":
      return { ...state, countPerPage: action.payload, currentPage: 1 };
    case "rowChanged":
      return {
        ...state,
        data: state.data.map((row) =>
          row._id === action.tripId ? { ...row, ...action.patch } : row,
        ),
      };
    case "filterChanged":
      return { ...state, [action.field]: action.value, currentPage: 1 };
    default:
      return state;
  }
};

const initialState = {
  data: [],
  loading: true,
  totalLength: 0,
  currentPage: 1,
  countPerPage: 10,
  searchInput: "",
  dbQuery: "",
  sortBy: "lastLeg",
  sortDirection: "desc",
  ownerFilters: [],
  ownerFilterOptions: [],
  ownerFiltersInput: [],
  clientFilters: [],
  clientFiltersInput: [],
  verifiedFilter: null,
};

const TripVerificationList = () => {
  const { user, getAccessTokenSilently } = useAuth0();

  const [state, dispatch] = useReducer(reducer, initialState);
  const abortControllerRef = useRef(null);

  const userRoles = (user && user["https://new.evojets.com/roles"]) || [];
  const canFilterByOwner = userRoles.some((role) =>
    ["Super Admin", "Account Director"].includes(role),
  );

  const getData = useCallback(async () => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    dispatch({ type: "setLoading", payload: true });

    try {
      const token = await getAccessTokenSilently();
      const response = await ai.auth(token).get("/api/trips/verification-list", {
        signal,
        params: {
          query: state.dbQuery,
          page: state.currentPage,
          countPerPage: state.countPerPage,
          sortBy: state.sortBy,
          sortDirection: state.sortDirection,
          ownerFilters: JSON.stringify(state.ownerFilters),
          clientFilters: JSON.stringify(state.clientFilters),
          verifiedFilter: state.verifiedFilter || undefined,
        },
      });

      dispatch({ type: "dataDownloaded", payload: response?.data });
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }
      console.error("Error fetching trip verification data:", error);
      toast.error("Failed to load trips");
      dispatch({ type: "setLoading", payload: false });
    }
  }, [
    getAccessTokenSilently,
    state.dbQuery,
    state.currentPage,
    state.countPerPage,
    state.sortBy,
    state.sortDirection,
    state.ownerFilters,
    state.clientFilters,
    state.verifiedFilter,
  ]);

  useEffect(() => {
    getData();
  }, [getData]);

  const handleRowChanged = useCallback((tripId, patch) => {
    dispatch({ type: "rowChanged", tripId, patch });
  }, []);

  const loadOwnerOptions = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const { data } = await ai.auth(token).get("/api/users/roles/sales");
      const options = [{ label: "Unassigned", value: "null" }, ...(data?.users || []).map((u) => ({
        value: u.user_id,
        label: u.name,
      }))];
      dispatch({ type: "fieldChanged", field: "ownerFilterOptions", value: options });
    } catch (error) {
      console.error("Error fetching owner filter options:", error);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (!canFilterByOwner) {
      return;
    }

    loadOwnerOptions();
  }, [canFilterByOwner, loadOwnerOptions]);

  const debouncedSearch = useDebouncedCallback((value) => {
    dispatch({ type: "filterChanged", field: "dbQuery", value });
  }, 500);

  const handleSearchChange = (e) => {
    const { value } = e.target;
    dispatch({ type: "fieldChanged", field: "searchInput", value });
    debouncedSearch(value);
  };

  const handleOwnerFilterChange = (selected) => {
    dispatch({ type: "fieldChanged", field: "ownerFiltersInput", value: selected || [] });
    dispatch({
      type: "filterChanged",
      field: "ownerFilters",
      value: (selected || []).map(({ value }) => value),
    });
  };

  const handleClientFilterChange = (selected) => {
    dispatch({ type: "fieldChanged", field: "clientFiltersInput", value: selected || [] });
    dispatch({
      type: "filterChanged",
      field: "clientFilters",
      value: (selected || []).map(({ value }) => value),
    });
  };

  const handleStatusFilterChange = (selected) => {
    dispatch({ type: "filterChanged", field: "verifiedFilter", value: selected?.value || null });
  };

  const loadClients = useCallback(
    async (inputValue) => {
      try {
        const token = await getAccessTokenSilently();
        const { data } = await ai
          .auth(token)
          .get("/api/prospects/search", {
            params: { q: inputValue, limit: PROSPECT_SEARCH_LIMIT },
          });
        return (data?.prospects ?? []).map(({ _id, firstName, lastName, emails }) => ({
          value: _id,
          label: [[firstName, lastName].filter(Boolean).join(" ").trim(), emails?.[0]].filter(Boolean).join(" - "),
        }));
      } catch (error) {
        console.error("Error searching clients:", error);
        return [];
      }
    },
    [getAccessTokenSilently],
  );

  const debouncedLoadClients = useDebouncedCallback((inputValue, callback) => {
    loadClients(inputValue).then(callback).catch(() => callback([]));
  }, 500);

  const handlePageChange = (page) => {
    dispatch({ type: "pageChanged", payload: page });
  };

  const handleRowsPerPageChange = (newPerPage) => {
    dispatch({ type: "rowsPerPageChanged", payload: newPerPage });
  };

  const handleSort = (column, sortDirection) => {
    if (!column?.sortField) return;
    dispatch({ type: "filterChanged", field: "sortBy", value: column.sortField });
    dispatch({
      type: "fieldChanged",
      field: "sortDirection",
      value: sortDirection,
    });
  };

  const columns = useMemo(
    () => [
      {
        name: "Trip #",
        cell: (row) => {
          return (
            <Link className="ellipsis-cell" to={`/trips/${row._id}`} target="_blank" rel="noopener noreferrer">
              <Tooltip title={row.tripTag} arrow>
                {row.tripTag}
              </Tooltip>
            </Link>
          );
        },
        width: "110px",
      },
      {
        name: "Owner",
        selector: (row) => shortenName(row.owner?.name) || "N/A",
        minWidth: "80px",
        width: "auto",
        maxWidth: "160px",
      },
      {
        name: "Client",
        id: "clientName",
        selector: (row) => row.clientName || "N/A",
        sortable: true,
        sortField: "clientName",
        minWidth: "100px !important",
        width: "auto",
        maxWidth: "220px",
      },
      {
        name: "Type",
        id: "tripType",
        selector: (row) => row.tripType || "-",
        width: "70px",
      },
      {
        name: "First Leg",
        id: "firstLeg",
        selector: (row) => formatLeg(row.firstLeg),
        sortable: true,
        sortField: "firstLeg",
        minWidth: "150px",
        maxWidth: "200px",
      },
      {
        name: "Last Leg",
        id: "lastLeg",
        selector: (row) => formatLeg(row.lastLeg),
        sortable: true,
        sortField: "lastLeg",
        minWidth: "150px",
        maxWidth: "200px",
      },
      {
        name: "Evo Contract",
        selector: (row) => formatCurrency(row.signedContractsFlightCharges),
        width: "135px",
      },
      {
        name: "OFRs",
        selector: (row) => formatCurrency(row.signedOFRsFlightCharges),
        width: "135px",
      },
      {
        name: "Stage",
        selector: (row) => STAGE_LABELS[row.displayStage] || STAGE_LABELS[row.stage] || "-",
        width: "105px",
      },
      {
        name: "Status",
        selector: (row) => (row.verified ? "Verified" : "Not Verified"),
        width: "100px",
      },
      {
        name: "Locked",
        selector: (row) => (row.lockedComputed ? "Yes" : "No"),
        width: "85px",
      },
    ],
    [],
  );

  return (
    <div id="trip-verification" className="page-container">
      <PageHeaderRowSearch
        h1="Trip Verification"
        pretitle="Business"
        breadcrumbs={[{ link: "/trips", label: "Trips" }]}
        extraClasses="page-header-trips-l1"
        rightColumn={
          <div id="trip-actions-row">
            <input
              type="text"
              onChange={handleSearchChange}
              value={state.searchInput}
              autoComplete="off"
              className="lead-search"
              placeholder="Search Trips"
              id="searchFlights"
            />

            {canFilterByOwner && (
              <Select
                options={state.ownerFilterOptions}
                isMulti
                className="owner-filter"
                placeholder="Filter by Owner"
                value={state.ownerFiltersInput}
                onChange={handleOwnerFilterChange}
                components={COMPACT_SELECT_COMPONENTS}
                isClearable
                closeMenuOnSelect={false}
              />
            )}

            <AsyncSelect
              isMulti
              className="owner-filter"
              placeholder="Filter by Client"
              loadOptions={(inputValue, callback) =>
                debouncedLoadClients(inputValue, callback)
              }
              value={state.clientFiltersInput}
              onChange={handleClientFilterChange}
              components={COMPACT_SELECT_COMPONENTS}
              isClearable
              closeMenuOnSelect={false}
            />

            <Select
              options={STATUS_FILTER_OPTIONS}
              className="stage-filter"
              placeholder="Filter by Status"
              value={
                STATUS_FILTER_OPTIONS.find((o) => o.value === state.verifiedFilter) || null
              }
              onChange={handleStatusFilterChange}
              isClearable
            />
          </div>
        }
      />

      <DataTable
        columns={columns}
        data={state.data}
        keyField="_id"
        noHeader
        highlightOnHover
        responsive
        expandableRows
        expandableRowsComponent={<TripVerificationExpanded onRowChanged={handleRowChanged}/>}
        conditionalRowStyles={conditionalRowStyles}
        progressPending={state.loading}
        progressComponent={<Loader/>}
        sortServer
        defaultSortFieldId="lastLeg"
        defaultSortAsc={false}
        onSort={handleSort}
        pagination
        paginationServer
        paginationPerPage={state.countPerPage}
        paginationRowsPerPageOptions={PER_PAGE_OPTIONS}
        paginationTotalRows={state.totalLength}
        paginationDefaultPage={state.currentPage}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handleRowsPerPageChange}
        noDataComponent={<div className="p-20">There are no records to display</div>}
      />
    </div>
  );
};

export default TripVerificationList;
